import { useState } from 'react';
import {
    Typography, List, ListItem, Chip,
    ListItemText, IconButton, Button, Box,
    Menu, MenuItem, Divider, CircularProgress,
    Link
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useNotifications, useDialog } from 'hooks';
import { BasePage } from 'components';

export const NotificationsPage = () => {
    const navigate = useNavigate();
    const { confirm } = useDialog();
    const {
        notifications,
        unreadCount,
        markAsRead,
        markAllAsRead,
        clearNotification,
        clearAllNotifications
    } = useNotifications();

    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedNotif, setSelectedNotif] = useState(null);
    const [loading, setLoading] = useState(false);

    const handleMenuOpen = (event, notif) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setSelectedNotif(notif);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedNotif(null);
    };

    const handleMarkAsRead = async () => {
        if (selectedNotif && !selectedNotif.read) {
            await markAsRead(selectedNotif.id);
        }
        handleMenuClose();
    };

    const handleClearNotification = async () => {
        if (selectedNotif) {
            await clearNotification(selectedNotif.id);
            handleMenuClose();
        }
    };

    const handleMarkAllAsRead = async () => {
        if (unreadCount === 0) return;
        setLoading(true);
        try {
            await markAllAsRead();
        } catch (error) {
            console.error('Error marking all as read:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleClearAll = async () => {
        if (notifications.length === 0) return;
        if (!confirm('Clear all notifications? This cannot be undone.')) return;

        const result = await confirm({
            title: "Clear all notifications",
            description: `Clear all notifications? This cannot be undone.`,
            confirmLabel: "Clear all",
            cancelLabel: "Cancel"
        });

        if (result) {
            setLoading(true);
            try {
                await clearAllNotifications();
            } catch (error) {
                console.error('Error clearing all:', error);
            } finally {
                setLoading(false);
            }
        }

        return;
    };

    const handleNotificationClick = async (notif) => {
        // Mark as read
        if (!notif.read) {
            await markAsRead(notif.id);
        }

        // Navigate to context
        if (notif.contextType === 'restaurant') {
            navigate(`/restaurant/${notif.restaurantId}`);
        } else if (notif.contextType === 'food') {
            navigate(`/food/edit/${notif.contextId}`);
        }
    };


    const formatNotificationStatus = (type) => {
        if (type.includes('approved')) {
            return 'Approved';
        }
        if (type.includes('rejected')) {
            return 'Rejected';
        }
        return '';
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '';
        const date = timestamp.toDate();
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
        if (diffMins < 10080) return `${Math.floor(diffMins / 1440)}d ago`;
        return date.toLocaleDateString();
    };

    return (
        <BasePage>
            {/* Header with title and buttons */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                mb: 3
            }}>
                {/* Title with badge */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h6">
                        Notifications
                    </Typography>
                    {unreadCount > 0 && (
                        <Chip
                            label={`${unreadCount} new`}
                            color="primary"
                            size="small"
                        />
                    )}
                </Box>

                {/* Action buttons */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {unreadCount > 0 && (
                        <Button
                            startIcon={loading ? <CircularProgress size={16} /> : <DoneAllIcon />}
                            onClick={handleMarkAllAsRead}
                            disabled={loading}
                            variant="outlined"
                            size="small"
                        >
                            Mark all read
                        </Button>
                    )}
                    {notifications.length > 0 && (
                        <Button
                            startIcon={loading ? <CircularProgress size={16} /> : <ClearAllIcon />}
                            onClick={handleClearAll}
                            disabled={loading}
                            color="error"
                            variant="outlined"
                            size="small"
                        >
                            Clear all
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Notifications List */}
            {notifications.length === 0 ? (
                <Box sx={{ p: 8, textAlign: 'center' }}>
                    <Typography variant="h6" color="text.secondary">
                        No notifications
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        You're all caught up!
                    </Typography>
                </Box>
            ) : (
                <List disablePadding sx={{ p: 0 }}>
                    {notifications.map((notif) => (
                        <ListItem
                            key={notif.id}
                            disablePadding
                            disableGutters
                            onClick={() => handleNotificationClick(notif)}
                            sx={{
                                mb: 1,
                                borderRadius: 1,
                                transition: 'all 0.2s',
                                color: "inherit",
                                '&:hover': {
                                    cursor: 'pointer',
                                    bgcolor: 'action.hover',
                                    transform: 'translateX(4px)'
                                }
                            }}
                            secondaryAction={
                                <IconButton
                                    edge="end"
                                    onClick={(e) => handleMenuOpen(e, notif)}
                                >
                                    <MoreVertIcon />
                                </IconButton>
                            }
                        >
                            <ListItemText
                                disableTypography
                                primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                        <Typography
                                            variant="subtitle1"
                                            sx={{
                                                fontWeight: notif.read ? 'normal' : 'bold',
                                                flex: 1
                                            }}
                                        >
                                            {notif.title}
                                        </Typography>
                                        {!notif.read && (
                                            <Chip
                                                label="New"
                                                size="small"
                                                color="primary"
                                            />
                                        )}
                                    </Box>
                                }
                                secondary={
                                    <Box>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{
                                                fontWeight: notif.read ? 'normal' : 500,
                                                mb: 0.5
                                            }}
                                        >
                                            {notif.message}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {/* Calculate status and color */}
                                            {(() => {
                                                const statusText = formatNotificationStatus(notif.type);
                                                if (!statusText) return null;

                                                // Determine color based on status
                                                const statusColor = statusText === 'Approved' ? 'success.main' : 'error.main';

                                                return (
                                                    <>
                                                        {/* Apply color and bolding */}
                                                        <Typography
                                                            component="span"
                                                            variant="caption"
                                                            sx={{ color: statusColor, fontWeight: 700 }}
                                                        >
                                                            {statusText}
                                                        </Typography>
                                                        {' • '}
                                                    </>
                                                );
                                            })()}

                                            {notif.contextTitle && (
                                                <>
                                                    <Link
                                                        component={RouterLink}
                                                        to={`/restaurant/${notif.restaurantId}/deal/${notif.dealId}`}
                                                        underline="hover"
                                                        color="primary"
                                                        sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                                                    >
                                                        {notif.contextTitle}
                                                    </Link>
                                                    {' • '}
                                                </>
                                            )}

                                            {formatTimestamp(notif.createdAt)}
                                            {notif.triggeredBy && (
                                                <>
                                                    {' • by '}
                                                    {notif.triggeredBy.name}
                                                </>
                                            )}
                                        </Typography>
                                    </Box>
                                }
                            />
                        </ListItem>
                    ))}
                </List>
            )}

            {/* Context Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                {selectedNotif && !selectedNotif.read && (
                    <MenuItem onClick={handleMarkAsRead}>
                        <DoneAllIcon sx={{ mr: 1 }} /> Mark as read
                    </MenuItem>
                )}
                {selectedNotif && !selectedNotif.read && <Divider />}
                <MenuItem onClick={handleClearNotification}>
                    <DeleteIcon sx={{ mr: 1 }} /> Clear notification
                </MenuItem>
            </Menu>
        </BasePage>
    );
};